/**
 * Discount-value units for the promotion form.
 *
 * The API stores a percent discount in BASIS POINTS — 2000 means 20%. That is stated in
 * three places server-side: loyalty's validator ("percent must not exceed 10000 basis
 * points"), its `percentOf(amount, bps)`, and the GraphQL field comment ("Basis points
 * (Percent) or int64 minor units (FixedAmount)").
 *
 * The form passed that number straight through in both directions, labelled only "Discount
 * value". So a seeded 20% promotion displayed as "2000" next to "Percent".
 *
 * THE DISPLAY IS THE HARMLESS HALF. 2000% is absurd enough that nobody acts on it. The
 * damage is the round trip, which is silent and plausible both ways:
 *
 *   - an operator "correcting" 2000 to 20 stores 20 bps = 0.2%
 *   - an operator creating a 20% promotion types 20 and gets 0.2%
 *
 * Neither errors. Both pass server validation, because 20 is a legal basis-point value.
 * This is the failure `slaPolicies.ts` already names for durations: "making an admin do
 * that arithmetic is how a policy ends up an order of magnitude out with nothing to catch
 * it — the API accepts any positive integer."
 *
 * These helpers mirror that file's shape deliberately: one function per direction, the
 * storage unit never reaching the form.
 *
 * FIXED-AMOUNT VALUES ARE NOT CONVERTED. The same field carries minor units for a
 * fixed_amount discount, and the admin panel displays money amounts raw throughout
 * (`addCommaToPrice` groups thousands and divides nothing). Converting one and not the
 * other here would make this file the odd one out rather than the fix.
 */

/** The API rejects anything above this — 10000 bps = 100%. */
export const MAX_DISCOUNT_BASIS_POINTS = 10000

/** Basis points carry two decimal places of percent: 1250 bps = 12.5%. */
const BASIS_POINTS_PER_PERCENT = 100

/**
 * Storage → form. Renders the shortest exact representation: 2000 → "20", 1250 → "12.5",
 * 1205 → "12.05".
 *
 * Returns '' for a value that is not a number, so a malformed API response leaves the field
 * empty rather than showing NaN — the operator then sees a field they must fill, which is
 * the recoverable state.
 */
export function basisPointsToPercent(basisPoints: string | number): string {
  if (typeof basisPoints === 'string' && !basisPoints.trim()) return ''
  // The empty check above is separate and load-bearing: Number('') is 0, not NaN, so an
  // absent value would otherwise render as "0" — a field that reads as a deliberate 0%
  // discount rather than as one nobody has filled in.
  const bps =
    typeof basisPoints === 'number' ? basisPoints : Number(basisPoints.trim())
  if (!Number.isFinite(bps)) return ''
  // toFixed(2) then strip trailing zeros, rather than plain division: 1205 / 100 is
  // 12.049999999999999 in binary floating point, and "12.049999999999999" in a percent
  // field is worse than the bug being fixed.
  return String(Number((bps / BASIS_POINTS_PER_PERCENT).toFixed(2)))
}

/**
 * Form → storage. "20" → 2000, "12.5" → 1250.
 *
 * Returns null when the input is not a usable percent, so the caller can surface a field
 * error instead of submitting a silently-wrong number. That is the whole point: the
 * previous code submitted whatever was typed.
 */
export function percentToBasisPoints(percent: string): number | null {
  const trimmed = percent.trim()
  if (!trimmed) return null
  const value = Number(trimmed)
  if (!Number.isFinite(value) || value < 0) return null
  const bps = Math.round(value * BASIS_POINTS_PER_PERCENT)
  if (bps > MAX_DISCOUNT_BASIS_POINTS) return null
  return bps
}

/**
 * Whether a typed percent is representable without silently losing precision.
 *
 * 12.345% is 1234.5 basis points, which the API cannot store. Rounding it quietly would
 * reintroduce the class of bug this file exists to close — a value that is not what the
 * operator typed, with nothing saying so.
 */
export function isPercentPrecisionExact(percent: string): boolean {
  const value = Number(percent.trim())
  if (!Number.isFinite(value)) return false
  const scaled = value * BASIS_POINTS_PER_PERCENT
  return Math.abs(scaled - Math.round(scaled)) < Number.EPSILON * 1000
}

/**
 * One display string for a discount, used everywhere a promotion is shown.
 *
 * The list previously rendered `{discountValue} {currency} ({discountType})`, which for a
 * percent discount produced "2000 IRR (Percent)" — the raw basis points AND a currency
 * appended to a percentage. Centralised here so the two surfaces cannot drift again: the
 * builder and the list were wrong in the same way and were fixed separately once already.
 */
export function formatDiscount(
  discountType: string,
  discountValue: string,
  currency: string
): string {
  if (discountType === 'Percent') {
    const percent = basisPointsToPercent(discountValue)
    // No currency. A percentage is not denominated in anything, and printing one was the
    // most visibly wrong part of the original.
    return percent ? `${percent}%` : ''
  }
  // Labelled as minor units rather than converted. The app displays money raw throughout
  // (addCommaToPrice groups thousands and divides nothing), so converting here alone would
  // make promotions disagree with every other amount on screen.
  //
  // But rendering `5000000 IRR` unqualified is what let the form's helper text claim the
  // field was "in the selected currency" — the display corroborated the wrong label. Say
  // which unit it is, so neither reading is available.
  return discountValue
    ? `${discountValue} ${currency} (minor units)`.trim()
    : ''
}

/** A row that a promotion can be scoped by, before it becomes a picker option. */
export type ScopeRow = {
  /** The value stored and sent back to the server. Opaque by design — see below. */
  code?: string | null
  /** The readable form of the same code, sent alongside it for display. */
  displayCode?: string | null
  name?: string | null
}

/**
 * Turns scope rows into picker options labelled with what an operator recognises.
 *
 * `code` is DELIBERATELY opaque: the BFF encrypts internal codes so they never leave the
 * system in plaintext. Using it as the label is what put `acc_YZe3lGrC…`, `rt_yWiBVl58Kcs…`
 * and `rp_lw859sQMfQkod…` on the Create-promotion screen, and why nobody could pick a room
 * type from that list. `displayCode` is the readable form the server sends alongside for
 * exactly this purpose; every response carries both.
 *
 * The opaque code is NOT a label fallback. Falling back to it would put ciphertext back on
 * screen the moment a display code is missing, which is the reported bug. A row with neither
 * a name nor a display code is dropped instead: an unlabelled option is one an operator
 * cannot choose correctly, and selectable-then-wrong is worse than absent — the same
 * reasoning already applied to rows with no code at all, which cannot be stored and produce
 * a chip that vanishes on the next render.
 */
export function toScopeOptions(
  rows: ScopeRow[]
): Array<{ label: string; value: string }> {
  return rows
    .filter(
      (row): row is ScopeRow & { code: string } =>
        Boolean(row.code) && Boolean(row.displayCode || row.name)
    )
    .map((row) => ({
      value: row.code,
      label:
        row.name && row.displayCode
          ? `${row.name} (${row.displayCode})`
          : ((row.displayCode || row.name) as string)
    }))
}
